import { inject, Injectable, signal } from '@angular/core';
import { EditedCustomRoute, EditedPoint, EditedStatus, GetSuggestedPointsBody } from '../interfaces/edit-route.interface';
import { catchError, map, Observable, Subject, takeUntil, throwError } from 'rxjs';
import { CustomEditedRouteResponse, GetSuggestedPointsResponse } from '../interfaces/edit-route.response';
import { RestService } from 'src/app/services/rest.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { PointType } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { updateCustomEditedRouteBodyAdapter } from '../adapters/update-custom-edited-route-body.adapter';
import { CustomRouteResponseCompleted } from 'src/app/pages/custom-route/interfaces/custom-route.response';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class EditRouteService {
  // Service injections
  private _http = inject(HttpClient);
  private _restService = inject(RestService);
  private _customRouteService = inject(CustomRouteService);
  
  // State service signals
  private _token = this._customRouteService.token;
  private _customRouteData =  this._customRouteService.customRouteData;
  
  // State signals
  private _customRouteEditedDataToSend = signal<EditedCustomRoute | null>(null);
  public customRouteEditedDataToSend = this._customRouteEditedDataToSend.asReadonly();
  
  private _customRouteEditedDataToRender = signal<EditedCustomRoute | null>(null);
  public customRouteEditedDataToRender = this._customRouteEditedDataToRender.asReadonly();
  
  private _initialStateCustomRouteEditedDataToRender = signal<EditedCustomRoute | null>(null);
  public initialStateCustomRouteEditedDataToRender = this._initialStateCustomRouteEditedDataToRender.asReadonly();

  private _customEditedRouteResponse = signal<CustomEditedRouteResponse | null>(null);
  public customEditedRouteResponse = this._customEditedRouteResponse.asReadonly();
  
  private _isEditRouteRegisterOpen = signal<boolean>(false);
  public isEditRouteRegisterOpen = this._isEditRouteRegisterOpen.asReadonly();

  private _isLoadingResourcesByIdOnEmptySpace = signal<number>(null);
  public isLoadingResourcesByIdOnEmptySpace = this._isLoadingResourcesByIdOnEmptySpace.asReadonly();

  private _isLoadingResourcesOnExistingItem = signal<boolean>(false);
  public isLoadingResourcesOnExistingItem = this._isLoadingResourcesOnExistingItem.asReadonly();

  private cancelSuggestedPoints$ = new Subject<void>();
  
  // Setters
  public setCustomRouteditedDataToSend(data: EditedCustomRoute | null): void {
    this._customRouteEditedDataToSend.set(data);
  }
  
  public setCustomRouteditedDataToRender(data: EditedCustomRoute | null): void {
    this._customRouteEditedDataToRender.set(data);
  }
  
  public setInitialStateCustomRouteditedDataToRender(data: EditedCustomRoute | null): void {
    this._initialStateCustomRouteEditedDataToRender.set(data);
  }
  
  public setCustomEditedRouteResponse(response: CustomEditedRouteResponse | null): void {
    this._customEditedRouteResponse.set(response);
  }

  public setIsEditRouteRegisterOpen(isOpen: boolean): void {
    this._isEditRouteRegisterOpen.set(isOpen);
  }

  public setIsLoadingResourcesByIdOnEmptySpace(isLoadingId: number): void {
    this._isLoadingResourcesByIdOnEmptySpace.set(isLoadingId);
  }

  public setIsLoadingResourcesOnExistingItem(isLoading: boolean): void {
    this._isLoadingResourcesOnExistingItem.set(isLoading);
  }

  public cancelSuggestedPointsRequests(): void {
    this.setIsLoadingResourcesByIdOnEmptySpace(null);
    this.setIsLoadingResourcesOnExistingItem(false);
    this.cancelSuggestedPoints$.next();
  }

  // Methods
  /**
   * 
   * @param body 
   * @returns 
   */
  public getSuggestedPoints(body: GetSuggestedPointsBody) : Observable<GetSuggestedPointsResponse> {
    const url = `${this._restService.apiUrlBase}/api/get-suggested-items`;
    
    return this._http.post<GetSuggestedPointsResponse>(url, body, { observe: 'response' })
      .pipe(
        takeUntil(this.cancelSuggestedPoints$),
        map((httpResponse) => {
          const status = httpResponse.status;
          const responseBody = httpResponse.body;
          return {
            status,
            ...(responseBody as object)
          } as GetSuggestedPointsResponse;
        }),
        catchError((httpError: HttpErrorResponse) => {
          console.error(`Error fetching suggested points: ${httpError.error.error}`);
          return throwError(() => new Error('HTTP request failed', { cause: httpError }));
        })
      )
  }
  
  /**
   * 
   * @param newPoint 
   * @param orderIndex 
   * @param dayIndex 
   * @returns 
   */
  addPointToEditedCustomRouteData(
    newPoint: EditedPoint, 
    orderIndex: number, 
    dayIndex: number
  ): void {
    if (!this._customRouteData() || !this._customRouteEditedDataToRender()) return;

    const currentData = this._customRouteEditedDataToRender();

    const updatedData: EditedCustomRoute = {
      ...currentData,
      dailyRoutes: currentData.dailyRoutes.map((dailyRoute, idx) => {
        if (idx !== dayIndex) return dailyRoute;

        const updatedPoints = [
          ...dailyRoute.points.slice(0, orderIndex),
          { ...newPoint, status: EditedStatus.ADDED },
          ...dailyRoute.points.slice(orderIndex)
        ];

        return {
          ...dailyRoute,
          points: updatedPoints
        };
      })
    };

    this._customRouteEditedDataToSend.set(updatedData);

    if(!environment.production) {
      console.log(`📌 Added new point at index ${orderIndex} on day ${dayIndex}:`, newPoint);
      console.log('📝 Updated Edited Custom Route to Send:', this._customRouteEditedDataToSend());
    }
  }
  
  /**
   * 
   * @param pointToReplace 
   * @param pointToBeReplaced 
   * @param dayIndex 
   * @returns 
   */
  replacePointToEditedCustomRouteData(
    pointToReplace: EditedPoint, 
    pointToBeReplaced: EditedPoint,
    dayIndex: number
  ): void {
    if (!this._customRouteData() || !this._customRouteEditedDataToSend()) return;

    const currentData = this._customRouteEditedDataToSend();
    const dailyRoute = currentData.dailyRoutes[dayIndex];
    if (!dailyRoute) return;

    const realIndex = dailyRoute.points.findIndex(point => point.id === pointToBeReplaced.id);
    if (realIndex === -1) return;

    // Usar realIndex en lugar de orderIndex
    const updatedPoints = [...dailyRoute.points];

    const originalPoint = updatedPoints[realIndex];

    updatedPoints.splice(realIndex, 1,
      {
        ...pointToBeReplaced,
        status: EditedStatus.OLD,
        id: originalPoint.id,
      },
      {
        ...pointToReplace,
        status: EditedStatus.UPDATED,
      }
    );

    const newEditedCustomRoute: EditedCustomRoute = {
      ...currentData,
      dailyRoutes: currentData.dailyRoutes.map((route, i) => i === dayIndex
        ? { ...route, points: updatedPoints }
        : route
      ),
    };

    this._customRouteEditedDataToSend.set(newEditedCustomRoute);

    if(!environment.production) {
      console.log('Updated Edited Custom Route Data to Send after replacement:', this._customRouteEditedDataToSend());
    }
  }

  /**
   * 
   * @param pointId 
   * @param pointType 
   * @returns 
   */
  deletePointToEditedCustomRouteData(pointId: number, pointType: PointType): void {
    if (!this._customRouteData() || !this._customRouteEditedDataToSend()) return;
    
    const currentData = this._customRouteEditedDataToSend();
    if (!currentData) return;

    const newDailyRoutes = currentData.dailyRoutes.map((dailyRoute) => {      
      const pointToDelete = dailyRoute.points.find(point => point.id === pointId && point.type === pointType);
      if (!pointToDelete) return dailyRoute;  
      
      // If the point is added, remove it from the array
      if(pointToDelete.status === EditedStatus.ADDED) {
        return {
          ...dailyRoute,
          points: dailyRoute.points.filter(point => point.id !== pointId && point.type === pointType)
        };
      }

      // If the point is not added, mark it as deleted
      return {
        ...dailyRoute,
        points: dailyRoute.points.map(point =>
          point.id === pointId && point.type === pointType
            ? { ...point, status: EditedStatus.DELETED }
            : point
        )
      };
    });

    const newEditedCustomRoute: EditedCustomRoute = {
      ...currentData,
      dailyRoutes: newDailyRoutes,
    };

    this._customRouteEditedDataToSend.set(newEditedCustomRoute);

    if(!environment.production) {
      console.log(`Point with ID ${pointId} marked as deleted`);
      console.log('Updated Edited Custom Route To Send after deletion:', newEditedCustomRoute);
    }
  }
  
  /**
   * 
   * @param pointId 
   * @param pointType 
   * @param dayIndex 
   * @returns 
   */
  recoverPointToEditedCustomRouteData(pointId: number, pointType: PointType, dayIndex: number): void {
    if (!this._customRouteData() || !this._customRouteEditedDataToSend()) return;

    const currentData = this._customRouteEditedDataToSend();
    
    const updatedData: EditedCustomRoute = {
      ...currentData,
      dailyRoutes: currentData.dailyRoutes.map((dailyRoute, idx) => {
        if (idx !== dayIndex) return dailyRoute;

        const recoveredPoints = dailyRoute.points.map(point => {
          const isSamePoint = point.id === pointId && point.type === pointType;
          
          // recover the point if it was deleted
          if (isSamePoint && point.status === EditedStatus.DELETED) {
            // Elimina la propiedad status
            const { status, ...rest } = point;
            return { ...rest };
          }
          
          // recover the point if it was updated
          if (isSamePoint && point.status === EditedStatus.UPDATED) {
            // update customRouteEditedDataToSend with the original point
            // 2) recover from the original data the point that has status old
            const updatedPointIndex = dailyRoute.points.findIndex(p => p.id === pointId && p.status === EditedStatus.UPDATED);
            if (updatedPointIndex < 0) return point;
            
            const oldPointIndex = updatedPointIndex - 1;
            if (oldPointIndex < 0) return point;
            
            const oldPoint = dailyRoute.points[oldPointIndex];
            //console.log('Recovered point:', oldPoint);
            
            // 3) remove the status from the old point
            const { status, ...rest } = oldPoint;
            //console.log('Recovered point without status:', { ...rest });
            
            return { ...rest }
          }
          
          // if the point is not deleted or updated, return it as is
          return point;
        });
        
        
        // TODO: ME GUSTARÍA NO TENER QUE HACER ESTO YA QUE NO DEBERÍA LLEGAR DUPLICADOS
        // if there are duplicated points, remove the one with status old
        const uniqueRecoveredPoints = recoveredPoints.filter((point, index, self) =>
          index === self.findIndex((p) => p.id === point.id && p.type === point.type && p.status !== EditedStatus.OLD)
        );
        
        return {
          ...dailyRoute,
          points: uniqueRecoveredPoints
        };
      })
    };

    this._customRouteEditedDataToSend.set(updatedData);

    if(!environment.production) {
      console.log('🔁 Edited Custom Route to Send after recovery:', this._customRouteEditedDataToSend());
    }
  }
  
  /**
   * Updates the custom route edited data to render
   * @returns void
   */
  updateCustomRouteEditedDataToRender(): void {
    const currentData = this._customRouteEditedDataToSend();
    if (!currentData) return;

    const updatedData: EditedCustomRoute = {
      ...currentData,
      dailyRoutes: currentData.dailyRoutes.map(dailyRoute => ({
        ...dailyRoute,
        points: dailyRoute.points.filter(point => point.status !== EditedStatus.OLD)
      }))
    };

    this._customRouteEditedDataToRender.set(updatedData);    
  }
  
  /**
   * Sends the new edited custom route data to the server
   * @param dayToShowIndex - The index of the day being edited
   * @returns Observable with the response
   */
  public sendNewEditedCustomRouteData(dayToShowIndex: number): Observable<CustomEditedRouteResponse> {
    const customRouteData = this._customRouteData();
    if (!customRouteData) {
      return throwError(() => new Error('Custom route data is not available'));
    }
    
    const editedCustomRouteDataToSend = this._customRouteEditedDataToSend();
    if (!editedCustomRouteDataToSend) {
      return throwError(() => new Error('Edited custom route data to send is not available'));
    }
    
    const editedCustomEditedRouteBody = updateCustomEditedRouteBodyAdapter(
      editedCustomRouteDataToSend, 
      this._token(), 
      dayToShowIndex
    );
    
    return this._customRouteService.updateCustomRouteData(editedCustomEditedRouteBody)
      .pipe(
        map((response: CustomEditedRouteResponse) => {
          // Update the daily route with the response
          this._customRouteService.updateDailyRoute(
            response as CustomRouteResponseCompleted, 
            dayToShowIndex
          );

          // Store the response in the signal
          this._customEditedRouteResponse.set(response);
          
          return response;
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(`Error updating custom route data: ${error}`);
          return throwError(() => error);
        })
      );
  }
  
  /**
   * Checks if there is a success list in the custom edited route response
   * @returns 
   */
  public checkIsSuccessListCustomEditedRouteResponse(): boolean {
    const response = this._customEditedRouteResponse();
    if (!response) return false;
    
    return response.success && response.success.length > 0;
  }
  
  /**
   * Checks if there is an error list in the custom edited route response
   * @returns 
   */
  public checkIsErrorListCustomEditedRouteResponse(): boolean {
    const response = this._customEditedRouteResponse();
    if (!response) return false;
    
    return response.error && response.error.length > 0;
  }
  
  /**
   * Checks if there is an only error list in the custom edited route response
   * @returns 
   */
  public checkIsOnlyErrorListCustomEditedRouteResponse(): boolean {
    return !this.checkIsSuccessListCustomEditedRouteResponse() && this.checkIsErrorListCustomEditedRouteResponse();
  }
}
